import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { DacScenarioService } from '../dac-scenarios.service';
import { DacScenariosRepository } from '@/store/dac-scenarios-state/dac-scenarios-state.repository';
import { PassingModeService } from '../../passing-mode/passing-mode.service';
import { DynamicForm, SelectOption } from '@/components/form-builder/form-builder.types';
import { editDacScenariosFormObject } from './utils/edit-dac-scenarios-form-object';
import { modeEvaluationFinalFormObject } from './utils/mode-evaluation-final-form-object';
import { modeEvaluationFinalDrawerObject } from './utils/mode-evaluation-final-drawer-object';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import { Button } from 'primeng/button';
import { ScenarioDto, ScenarioEtape, StepDto } from '@/types/scenarios.types';
import { dacScenariosSchema } from '@/validations/dac-screnarios.validation';
import { DataTableBuilderComponent } from '@/components/data-table-builder/data-table-builder.component';
import { DataTableRowActions, DynamicDataTable } from '@/components/data-table-builder/data-table.types';
import { getStepsDataTableStructure } from './utils/steps-data-table-object';
import { DrawerObject } from '@/components/drawer/drawer.types';
import { DrawerComponent } from '@/components/drawer/drawer.component';
import { DialogObject } from '@/components/dialog/dialog.types';
import { DialogComponent } from '@/components/dialog/dialog.component';
import { addStepDrawerObject } from './utils/add-step-drawer-object';
import { stepFormObject } from './utils/step-form-object';
import { forkJoin, Observable } from 'rxjs';
import { DocSubmissionService } from '../../doc-submission/doc-submission.service';
import { deleteStepDialogObject } from './utils/delete-step-dialog-object';
import { Step } from '@/types/scenarios.types';
import { ModeEvaluationService } from '@/services/mode-evaluation.service';

@Component({
  selector: 'sm-edit-dac-scenarios',
  imports: [PageHeaderComponent, FormBuilderComponent, Button, TranslateModule, DataTableBuilderComponent, DrawerComponent, DialogComponent],
  templateUrl: './edit-dac-scenarios.component.html',
  styleUrl: './edit-dac-scenarios.component.scss',
})
export class EditDacScenariosComponent implements OnInit {
  dacScenarioService = inject(DacScenarioService);
  dacScenariosRepository = inject(DacScenariosRepository);
  passingModeService = inject(PassingModeService);
  messageService = inject(MessageService);
  translate = inject(TranslateService);
  activatedRoute = inject(ActivatedRoute);
  modePassationService = inject(PassingModeService);
  docSubmissionService = inject(DocSubmissionService);
  modeEvaluationService = inject(ModeEvaluationService);

  @ViewChild('drawer') drawer!: DrawerComponent;
  drawerObject?: DrawerObject;

  @ViewChild('dialog') dialog!: DialogComponent;
  dialogObject?: DialogObject;

  passingModeOptions?: SelectOption[];

  breadcrumb = [
    {
      label: 'settings.dacScenarios.breadcrumb.parametres',
      routerLink: ['/settings'],
    },
    {
      label: 'settings.dacScenarios.breadcrumb.scenarios',
      routerLink: ['/settings', 'dac-scenarios'],
    },
    {
      label: 'settings.dacScenarios.edit.title',
      routerLink: ['/settings', 'dac-scenarios', 'edit'],
    },
  ];

  modePassationOptions?: SelectOption[];

  formObject: DynamicForm = editDacScenariosFormObject({
    store: this.dacScenariosRepository,
  });

  stepsOptions: SelectOption[] = [];
  dossiersOptions: SelectOption[] = [];

  stepFormObject: DynamicForm = stepFormObject({
    store: this.dacScenariosRepository,
    stepOptions: [],
    dossierOptions: [],
  });

  id = this.activatedRoute.snapshot.paramMap.get('id');

  rowActions: DataTableRowActions = {
    deleteAction: (row: StepDto) => {
      this.dialogObject = deleteStepDialogObject({
        action: () => {
          this.deleteStep(row);
          this.dialog.toggleVisible();
        },
        toggleVisibility: () => this.dialog.toggleVisible(),
        resetStepDto: () => this.resetStepDto(),
      });
      this.dialog.toggleVisible();
    },
  };

  dataTableObject: DynamicDataTable = getStepsDataTableStructure({
    rowActions: this.rowActions,
    createAction: () => {
      if (this.id) {
        this.loadAvailableSteps();
      }
      this.drawer.toggleVisible();
    }
  });
  data: ScenarioEtape[] = [];
  loading = false;

  ngOnInit() {
    this.loadData();
  }

  loadData() {
    this.loading = true;
    if (this.id) {
      this.dacScenarioService.getOne(+this.id).subscribe({
        next: response => {
          this.dacScenariosRepository.set('dto', {
            modePassationIds: response.modePassations.map(mode => mode.id),
            nom: response.nom,
            description: response.description,
            code: response.code,
          });
          this.dacScenariosRepository.set('response', response);
          this.dacScenariosRepository.setNested('dto', 'modeEvaluationFinalId', response.modeEvaluationFinal?.id || null);
          this.data = response.scenarioEtapes;
          this.loading = false;
          this.loadFormOptions(response.id);
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('settings.dacScenarios.message.error.loading'),
          });
        },
      });
    }
  }

  update() {
    const dto = this.dacScenariosRepository.get<ScenarioDto>('dto');
    const result = dacScenariosSchema.safeParse(dto);
    if (result.success) {
      this.dacScenarioService.update(this.dacScenariosRepository.getNested<number>('response', 'id'), dto).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('settings.dacScenarios.message.success.edited'),
          });
          this.dacScenariosRepository.reset();
          this.loadData();
        },
        error: (error: any) => {
          if (error.error.status === 400) {
            this.messageService.add({
              severity: 'error',
              summary: 'Erreur',
              detail: error.error.message,
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Erreur',
              detail: this.translate.instant('settings.dacScenarios.message.error.edited'),
            });
          }
        },
      });
    } else {
      this.dacScenariosRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  private loadFormOptions(scenarioId: number) {
    forkJoin({
      modePassation: this.modePassationService.list(),
      modeEvaluationFinal: this.dacScenarioService.getAvailableModeEvaluationFinal(scenarioId)
    }).subscribe(({ modePassation, modeEvaluationFinal }) => {
      this.modePassationOptions = (modePassation || []).map(modePassation => ({
        name: modePassation.libelle,
        code: Number(modePassation.id),
      }));

      this.dacScenariosRepository.set(
        'passingModeSelected',
        this.modePassationOptions.filter(option =>
          (this.dacScenariosRepository.getNested<number[]>('dto', 'modePassationIds') || []).includes(option.code as number)
        )
      );

      const modeEvaluationFinalOptions = (modeEvaluationFinal || []).map(mode => ({
        name: mode.description,
        code: mode.id,
      }));
      this.dacScenariosRepository.set('modeEvaluationFinalOptions', modeEvaluationFinalOptions);

      this.formObject = editDacScenariosFormObject({
        store: this.dacScenariosRepository,
        passingModeOptions: this.modePassationOptions,
      });
    });
  }

  openGlobalEvaluationFinalModal() {
    this.drawerObject = modeEvaluationFinalDrawerObject({
      formObject: modeEvaluationFinalFormObject({
        store: this.dacScenariosRepository,
        translate: this.translate,
      }),
      toggleVisibility: () => this.drawer.toggleVisible(),
      action: () => {
        this.updateModeEvaluationFinal();
      },
    });
    this.drawer.toggleVisible();
  }

  updateModeEvaluationFinal() {
    const scenarioId = this.dacScenariosRepository.getNested<number>('response', 'id');
    const modeEvaluationFinalId = this.dacScenariosRepository.getNested<number>('dto', 'modeEvaluationFinalId');

    const payload = {
      scenarioId,
      modeEvaluationFinalId: modeEvaluationFinalId || null
    };

    this.dacScenarioService.updateModeEvaluationFinal(payload).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('settings.dacScenarios.message.success.edited'),
        });
        this.drawer.toggleVisible();
        this.loadData();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.dacScenarios.message.error.edited'),
        });
      },
    });
  }

  deleteStep(row: StepDto) {
    const step = {
      etapeId: row.etapeId,
      typeDossierId: row.typeDossierId,
      orderEvaluation: row.orderEvaluation,
      isPublic: row.isPublic,
      isEliminatoire: row.isEliminatoire,
      hasSeuil: row.hasSeuil,
      hasPonderation: row.hasPonderation,
    };
    this.dacScenarioService.deleteStep(this.dacScenariosRepository.getNested<number>('response', 'id'), step).subscribe({
      next: () => {
        this.loadData();
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('settings.dacScenarios.message.success.deleted'),
        });
      },
      error: (error: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: error.error.message || this.translate.instant('settings.dacScenarios.message.error.deleted'),
        });
      },
    });
  }

  private loadStepsAndDossiers(steps$: Observable<any>, buildDrawer: (stepOptions: SelectOption[], dossierOptions: SelectOption[]) => void) {
    forkJoin({
      steps: steps$,
      dossiers: this.docSubmissionService.getAllFolderTypes(),
    }).subscribe(({ steps, dossiers }) => {
      const stepOptions = steps.map((step: Step) => ({
        name: step.nom,
        code: step.id,
        stepCode: step.code,
      }));

      const dossierOptions = dossiers.map(dossier => ({
        name: dossier.libelle,
        code: dossier.id,
      }));

      this.stepsOptions = stepOptions;
      this.dossiersOptions = dossierOptions;

      buildDrawer(stepOptions, dossierOptions);
    });
  }

  private loadAvailableSteps() {
    // Build a map of etapeCode -> isPublic from already-loaded scenario data
    const response: any = this.dacScenariosRepository.get('response');
    const existingEtapeMap = new Map<string, boolean>();
    if (response && response.scenarioEtapes) {
      for (const se of response.scenarioEtapes) {
        if (se.etapeCode && !existingEtapeMap.has(se.etapeCode)) {
          existingEtapeMap.set(se.etapeCode, se.isPublic);
        }
      }
    }


    this.loadStepsAndDossiers(this.dacScenarioService.getSteps(), (stepOptions, dossierOptions) => {
      this.drawerObject = addStepDrawerObject({
        formObject: stepFormObject({
          store: this.dacScenariosRepository,
          stepOptions,
          dossierOptions,
          existingEtapeMap,
          onTypeDossierChange: (selected: SelectOption) => this.onTypeDossierChange(selected),
        }),
        toggleVisibility: () => this.drawer.toggleVisible(),
        action: () => this.createStep(this.dacScenariosRepository.getNested<number>('response', 'id')),
        resetStepDto: () => this.resetStepDto(),
      });
    });
  }

  createStep(scenarioId: number) {
    const stepDto = this.dacScenariosRepository.get<StepDto>('stepDto');
    const stepSelected = this.dacScenariosRepository.get<SelectOption>('stepSelected');
    const dossierSelected = this.dacScenariosRepository.get<SelectOption>('dossierSelected');
    const modeEvaluationSelected = this.dacScenariosRepository.get<SelectOption>('modeEvaluationSelected');

    const payload: StepDto = {
      ...stepDto,
      etapeId: stepDto?.etapeId ?? (stepSelected?.code as number),
      typeDossierId: stepDto?.typeDossierId ?? (dossierSelected?.code as number),
      modeEvaluationId: stepDto?.modeEvaluationId ?? (modeEvaluationSelected?.code as number),
    };

    console.log("payload", payload);
    this.dacScenarioService.createStep(scenarioId, payload).subscribe({
      next: (updatedScenario: any) => {
        // Update the store's response immediately so that isPublic locking
        // works correctly if the user immediately opens the drawer again.
        this.dacScenariosRepository.set('response', updatedScenario);
        this.data = updatedScenario.scenarioEtapes;
        this.loadData();
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('settings.dacScenarios.message.success.added'),
        });
        this.drawer.toggleVisible();
        this.resetStepDto();
      },
      error: (error: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: error.error.message || this.translate.instant('settings.dacScenarios.message.error.added'),
        });
      },
    });
  }

  resetStepDto() {
    this.dacScenariosRepository.set('stepDto', undefined);
    this.dacScenariosRepository.set('stepSelected', undefined);
    this.dacScenariosRepository.set('dossierSelected', undefined);
    this.dacScenariosRepository.set('orderEvaluation', undefined);
    this.dacScenariosRepository.set('modeEvaluationSelected', undefined);
    this.dacScenariosRepository.set('modeEvaluationOptions', []);
  }

  onTypeDossierChange(selected: SelectOption) {
    this.dacScenariosRepository.set('modeEvaluationSelected', undefined);
    this.dacScenariosRepository.setNested('stepDto', 'modeEvaluationId', undefined);
    this.dacScenariosRepository.setNested('stepDto', 'hasSeuil', null, true);
    this.dacScenariosRepository.setNested('stepDto', 'hasPonderation', null, true);
    this.dacScenariosRepository.set('modeEvaluationOptions', []);

    const typeDossierId = selected?.code;
    if (typeof typeDossierId !== 'number') {
      return;
    }

    this.modeEvaluationService.getAllowedModesByTypeDossierId(typeDossierId).subscribe({
      next: modes => {
        const options = (modes ?? []).map(mode => ({
          name: mode.modeEvaluation.code,
          code: mode.modeEvaluation.id,
        }));
        this.dacScenariosRepository.set('modeEvaluationOptions', options);
      },
      error: () => {
        this.dacScenariosRepository.set('modeEvaluationOptions', []);
      },
    });
  }
}
